import { useSelector } from "react-redux";

import { type ComponentType, HealthStateValue, type NodeId, type ViewComponent } from "~/models/dto";

import { useTopologySnapshot } from "../../../app/state/topology/use-topology-snapshot";
import { selectTimestampDate } from "../../../app/state/view/timeline/TimelineSelectors";
import type { Api, FullComponent } from "../../../common/openapi";
import { queryFullComponent } from "../../../queries";
import { useQuery } from "../../../queries/react-query";

export interface SimpleStatefulComponent {
	id: number;
	name: string;
	typeName: string;
	icon?: string;
	identifiers: Array<string>;
	healthState: HealthStateValue | Api.HealthStateValue;
}

export const fullToSimpleStatefulComponent = (component: FullComponent): SimpleStatefulComponent => ({
	id: component.data.id!,
	name: component.data.name,
	typeName: component.typeName,
	icon: component.icon,
	identifiers: component.data.identifiers ?? [],
	healthState: component.data.health ?? HealthStateValue.Unknown,
});

export const viewToSimpleStatefulComponent = (
	component: ViewComponent,
	componentTypes: ReadonlyMap<string, ComponentType>,
): SimpleStatefulComponent => ({
	id: component.id!,
	name: component.name,
	typeName: componentTypes.get(component.typeIdentifier)?.name ?? "no type",
	icon: component.iconbase64 ?? componentTypes.get(component.typeIdentifier)?.iconbase64,
	identifiers: component.identifiers ?? [],
	healthState: component.state?.healthState ?? HealthStateValue.Unknown,
});

/**
 * Helper hook to retrieve a component at the current timestamp. It will try use the current topology view to fulfil the request,
 * avoiding querying the api.
 */
export const useSimpleComponentFromSnapshotOrApi = ({
	componentId,
}: {
	componentId: NodeId;
}): undefined | SimpleStatefulComponent => {
	const timestamp = useSelector(selectTimestampDate)?.valueOf();

	// Fast path (optimization). See if we can find the component in the current view. This is the typical case.
	const { data: simpleComponentInView, isLoading } = useTopologySnapshot({
		select: ({ components, componentTypes }) => {
			const componentInView = components.get(componentId);

			return componentInView == null ? undefined : viewToSimpleStatefulComponent(componentInView, componentTypes);
		},
	});

	// Otherwise go for a full component retrieval.
	const { data: component = simpleComponentInView } = useQuery({
		select: fullToSimpleStatefulComponent,
		enabled: !(isLoading || simpleComponentInView),

		...queryFullComponent({ componentIdOrIdentifier: componentId, timestamp }),
	});

	return component;
};
